/**
 * The portal's one calendar, mirroring services/shared/shared/calendar.py.
 *
 * Every date is shown on the business clock — Africa/Mogadishu (EAT, UTC+3,
 * no DST) — whatever zone the operator's browser is in, and always as
 * "10 Sep 2026" / "10 Sep 2026, 14:30".
 *
 * Two kinds of input:
 * - Date-only "YYYY-MM-DD" (due dates, start/end dates, birthdays) is a
 *   calendar fact: it is formatted from its Y/M/D, never through a time zone.
 * - Timestamps are instants. A value with Z/offset is exact; a naive one from
 *   the backend is UTC.
 */

export const BUSINESS_TIME_ZONE = 'Africa/Mogadishu';
export const BUSINESS_TIME_ZONE_ABBR = 'EAT';

// Mogadishu has been a fixed UTC+3 with no DST since 1950, so plain offset
// arithmetic is exact and independent of the browser's tz database.
const BUSINESS_OFFSET_MS = 3 * 60 * 60 * 1000;
const BUSINESS_OFFSET = '+03:00';
const DAY_MS = 86_400_000;

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export type DateInput = string | number | Date | null | undefined;

export interface CalendarDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

const pad = (n: number) => String(n).padStart(2, '0');

const toIsoDate = ({ year, month, day }: CalendarDate) =>
  `${year}-${pad(month)}-${pad(day)}`;

export function isDateOnly(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY.test(value.trim());
}

/** "2026-09-10" → {2026, 9, 10}; anything else (or an impossible date) → null. */
export function parseDateOnly(value: string | null | undefined): CalendarDate | null {
  const match = value ? DATE_ONLY.exec(value.trim()) : null;
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/**
 * Typed date input → "YYYY-MM-DD". Accepts the display form ("10 Sep 2026",
 * "10 september 2026"), ISO ("2026-09-10") and day-first numerics
 * ("10.09.2026", "10/09/2026").
 */
export function parseDateInput(text: string): string | null {
  const trimmed = text.trim().toLowerCase();
  let candidate: string | null = null;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  const numeric = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(trimmed);
  const named = /^(\d{1,2})\s+([a-z]{3})[a-z]*\.?,?\s+(\d{4})$/.exec(trimmed);

  if (iso) candidate = `${iso[1]}-${pad(+iso[2])}-${pad(+iso[3])}`;
  else if (numeric) candidate = `${numeric[3]}-${pad(+numeric[2])}-${pad(+numeric[1])}`;
  else if (named) {
    const month = MONTHS.findIndex((m) => m.toLowerCase() === named[2]);
    if (month >= 0) candidate = `${named[3]}-${pad(month + 1)}-${pad(+named[1])}`;
  }
  return candidate && parseDateOnly(candidate) ? candidate : null;
}

/**
 * The instant a value denotes. A date-only string is 00:00 EAT that day; a
 * naive timestamp is UTC.
 */
export function toInstant(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);

  const text = value.trim();
  if (DATE_ONLY.test(text)) return startOfBusinessDay(text);
  const normalised = text.includes('T') || !text.includes(' ') ? text : text.replace(' ', 'T');
  const parsed = new Date(HAS_ZONE.test(normalised) ? normalised : `${normalised}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

interface BusinessParts extends CalendarDate {
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

function businessParts(instant: Date): BusinessParts {
  const shifted = new Date(instant.getTime() + BUSINESS_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  };
}

function calendarDateOf(value: DateInput): CalendarDate | null {
  if (typeof value === 'string' && isDateOnly(value)) return parseDateOnly(value);
  const instant = toInstant(value);
  return instant ? businessParts(instant) : null;
}

/** The business-day date ("YYYY-MM-DD") a value falls on. */
export function businessDateOf(value: DateInput): string | null {
  const date = calendarDateOf(value);
  return date ? toIsoDate(date) : null;
}

/** Today's date in EAT, as "YYYY-MM-DD". */
export function businessToday(now: number | Date = Date.now()): string {
  return toIsoDate(businessParts(new Date(now)));
}

/** "10 Sep 2026". */
export function formatDate(value: DateInput, fallback = '—'): string {
  const date = calendarDateOf(value);
  if (!date) return fallback;
  return `${date.day} ${MONTHS[date.month - 1]} ${date.year}`;
}

/** "10 Sep" — only where the year is obvious from context (chart ticks). */
export function formatDayMonth(value: DateInput, fallback = '—'): string {
  const date = calendarDateOf(value);
  if (!date) return fallback;
  return `${date.day} ${MONTHS[date.month - 1]}`;
}

/** "14:30" (or "14:30:05") on the business clock. */
export function formatTime(value: DateInput, fallback = '—', seconds = false): string {
  const instant = toInstant(value);
  if (!instant) return fallback;
  const p = businessParts(instant);
  const hm = `${pad(p.hour)}:${pad(p.minute)}`;
  return seconds ? `${hm}:${pad(p.second)}` : hm;
}

/** "10 Sep 2026, 14:30" on the business clock. A date-only value stays a date. */
export function formatDateTime(value: DateInput, fallback = '—', seconds = false): string {
  if (isDateOnly(value)) return formatDate(value, fallback);
  const instant = toInstant(value);
  if (!instant) return fallback;
  return `${formatDate(instant)}, ${formatTime(instant, fallback, seconds)}`;
}

/** "10 Sep 2026, 14:30 EAT" — for tooltips and exports, where the zone must travel with the time. */
export function formatDateTimeWithZone(value: DateInput, fallback = '—'): string {
  const text = formatDateTime(value, '');
  if (!text) return fallback;
  return isDateOnly(value) ? text : `${text} ${BUSINESS_TIME_ZONE_ABBR}`;
}

const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

/** "Thursday" for a date-only (or instant, on its EAT day) value. */
export function formatWeekday(value: DateInput, fallback = '—'): string {
  const date = calendarDateOf(value);
  if (!date) return fallback;
  return WEEKDAYS[new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay()];
}

/** Date-only arithmetic: "2026-09-10" + 30 → "2026-10-10". */
export function addDays(date: string, days: number): string {
  const parsed = parseDateOnly(date);
  if (!parsed) throw new RangeError(`Not a YYYY-MM-DD date: ${date}`);
  const moved = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day + days));
  return toIsoDate({
    year: moved.getUTCFullYear(),
    month: moved.getUTCMonth() + 1,
    day: moved.getUTCDate(),
  });
}

/** Whole calendar days from `from` to `to` (both date-only); negative if `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  const a = parseDateOnly(from);
  const b = parseDateOnly(to);
  if (!a || !b) return Number.NaN;
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / DAY_MS
  );
}

export function isToday(value: DateInput, now: number | Date = Date.now()): boolean {
  const date = businessDateOf(value);
  return date !== null && date === businessToday(now);
}

export function isYesterday(value: DateInput, now: number | Date = Date.now()): boolean {
  const date = businessDateOf(value);
  return date !== null && date === addDays(businessToday(now), -1);
}

/** 00:00 EAT on a date-only day. */
export function startOfBusinessDay(date: string): Date {
  return new Date(`${toIsoDate(requireDate(date))}T00:00:00${BUSINESS_OFFSET}`);
}

/** 23:59:59 EAT on a date-only day — the last instant something dated that day is on time. */
export function endOfBusinessDay(date: string): Date {
  return new Date(`${toIsoDate(requireDate(date))}T23:59:59${BUSINESS_OFFSET}`);
}

function requireDate(date: string): CalendarDate {
  const parsed = parseDateOnly(date);
  if (!parsed) throw new RangeError(`Not a YYYY-MM-DD date: ${date}`);
  return parsed;
}

/**
 * An EAT wall-clock input ("2026-09-10" + "14:30", or a datetime-local
 * "2026-09-10T14:30") as an explicit instant: "2026-09-10T14:30:00+03:00".
 */
export function toEatInstant(date: string, time?: string): string {
  const [datePart, inlineTime] = date.split('T');
  const clock = (time ?? inlineTime ?? '00:00').trim();
  if (!/^\d{2}:\d{2}(:\d{2})?$/.test(clock)) {
    throw new RangeError(`Not an HH:mm time: ${clock}`);
  }
  const seconds = clock.length === 5 ? `${clock}:00` : clock;
  return `${toIsoDate(requireDate(datePart))}T${seconds}${BUSINESS_OFFSET}`;
}

/** The current EAT wall clock as a datetime-local value, "YYYY-MM-DDTHH:mm". */
export function businessNowLocalInput(now: number | Date = Date.now()): string {
  const p = businessParts(new Date(now));
  return `${toIsoDate(p)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Days between installments, as calendar.py's PERIOD_DAYS (30-day months). */
export const INSTALLMENT_PERIOD_DAYS: Readonly<Record<string, number>> = {
  daily: 1,
  weekly: 7,
  monthly: 30,
};

const RELATIVE_WINDOW_DAYS = 7;

/**
 * "5 minutes ago", "yesterday", "in 3 hours" in the UI language, and the full
 * date once the value is more than a week away. Days count EAT calendar days,
 * so "yesterday" means the business yesterday.
 */
export function formatRelative(
  value: DateInput,
  language = 'en',
  now: number | Date = Date.now(),
  fallback = '—'
): string {
  const instant = toInstant(value);
  if (!instant) return fallback;
  const nowMs = new Date(now).getTime();
  const rtf = relativeFormatter(language);
  const seconds = Math.round((instant.getTime() - nowMs) / 1000);
  const abs = Math.abs(seconds);

  if (abs < 45) return rtf.format(0, 'second');
  if (abs < 45 * 60) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 12 * 60 * 60) return rtf.format(Math.round(seconds / 3600), 'hour');

  const days = daysBetween(businessToday(nowMs), businessDateOf(instant) ?? '');
  if (days === 0) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (Math.abs(days) <= RELATIVE_WINDOW_DAYS) return rtf.format(days, 'day');
  return formatDate(instant, fallback);
}

const relativeFormatters = new Map<string, Intl.RelativeTimeFormat>();

function relativeFormatter(language: string): Intl.RelativeTimeFormat {
  let rtf = relativeFormatters.get(language);
  if (!rtf) {
    try {
      rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
    } catch {
      rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    }
    relativeFormatters.set(language, rtf);
  }
  return rtf;
}
